<script setup lang="ts">

const props = defineProps({
	name: {
		type: String,
		default: '',
	},
	value: {
		type: String,
		default: '',
		required: true,
	},
	id: {
		type: String,
		default: '',
		required: true,
	},
	modelValue: {
		type: String,
		default: '',
		required: true,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits<{ (e: 'update:modelValue', value: string | null): void; }>();
const inputElement = ref<HTMLElement | null>(null);

function handleInput() {
	emit('update:modelValue', props.value);
}

function handleClick() {
	inputElement.value?.click();
}

const isChecked = computed(() => {
	return props.modelValue === props.value;
});
</script>

<template>
  <label
    :for="id"
    :class="['radio-button-container', {'disabled': disabled, 'checked': isChecked}]"
  >
    <div class="label-wrapper">
      <input
        :id="id"
        ref="inputElement"
        :checked="isChecked"
        :value="value"
        :name="name"
        :disabled="disabled"
        class="radio"
        type="radio"
        @input="handleInput"
      >
      <div class="icon">
        <slot name="icon" />
      </div>
      <slot name="label" />
    </div>
    <div
      class="checkmark"
      @click="handleClick"
    >
      <CheckIcon
        v-if="isChecked"
        :color="ICON_COLOR.WHITE"
        stroke-width="3"
        width="10"
        height="10"
      />
    </div>
  </label>
</template>

<style lang="scss" scoped>
.radio-button-container {
	position: relative;
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: spacings-get(3) spacings-get(4);
	@include fonts-get(medium, text-sm);
	color: colors-get(gray, 700);
	@include border-radius-lg;
	border: 1px solid colors-get(gray, 200);
	background: colors-get(base, white);
	cursor: pointer;

	&:hover:not(.checked):not(.disabled) {
		border-color: colors-get(primary, 300);
		>.checkmark {
			background: colors-get(primary, 100);
			border: solid 1px colors-get(primary, 600);
		}
	}

	.label-wrapper {
		display: flex;
		align-items: center;
		gap: spacings-get(3);
		margin-bottom: 0;
	}

	&.disabled, &.disabled .checkmark {
		cursor: not-allowed;
		background-color: colors-get(gray, 50);
	}

	.radio {
		position: absolute;
		opacity: 0;
		height: 0;
		width: 0;
	}

	.icon {
		display: flex;
	}

	.checkmark {
		display: flex;
		align-items: center;
		justify-content: center;
		height: rem(16);
		width: rem(16);
		background-color: colors-get(base, white);
		border-radius: 50%;
		border: 1px solid colors-get(gray, 300);
		cursor: pointer;
	}

	&.checked {
		color: colors-get(primary, 800);
		border: 2px solid colors-get(primary, 600);
		background: colors-get(primary, 50);

		.checkmark {
			background-color: colors-get(primary, 600);
			border-color: colors-get(primary, 600);
		}
	}
}
</style>
